import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard } from '../api/client';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ManagerCard {
  id: number;
  name: string;
  current_score: number;
  score_trend: number;
  total_dialogues: number;
  key_issues: string[];
}

interface DashboardData {
  team_avg_score: number;
  team_score_trend: number;
  total_dialogues_today: number;
  missed_revenue: number;
  managers: ManagerCard[];
  alerts: any[];
  score_history: { date: string; score: number }[];
}

const avatarColors: Record<string, string> = {
  'А': '#6366f1', 'Б': '#8b5cf6', 'В': '#06b6d4', 'Г': '#10b981',
  'Д': '#f59e0b', 'Е': '#ef4444', 'Ж': '#ec4899', 'З': '#14b8a6',
  'И': '#6366f1', 'К': '#8b5cf6', 'Л': '#06b6d4', 'М': '#10b981',
  'Н': '#f59e0b', 'О': '#ef4444', 'П': '#ec4899', 'Р': '#14b8a6',
  'С': '#6366f1', 'Т': '#8b5cf6', 'У': '#06b6d4', 'Ф': '#10b981',
};

const getAvatarColor = (name: string) => {
  const firstChar = name.charAt(0).toUpperCase();
  return avatarColors[firstChar] || '#6366f1';
};

const getInitials = (name: string) => {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
};

const getScoreColor = (score: number) => {
  if (score >= 80) return '#10b981';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

const ManagerCardItem: React.FC<{ m: ManagerCard; onClick: () => void }> = ({ m, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const mColor = getScoreColor(m.current_score);
  const avatarBg = getAvatarColor(m.name);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 20,
        border: '1px solid #e2e8f0',
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hovered
          ? '0 8px 25px rgba(0,0,0,0.1)'
          : '0 1px 3px rgba(0,0,0,0.1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{
          width: 42, height: 42, borderRadius: '50%',
          background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 14, fontWeight: 700, color: '#fff', flexShrink: 0,
        }}>
          {getInitials(m.name)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{m.name}</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>
            {m.total_dialogues} диалогов
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={{ fontSize: 28, fontWeight: 700, color: mColor }}>{m.current_score}</span>
        <span style={{ fontSize: 13, color: '#94a3b8' }}>/ 100</span>
        {m.score_trend !== 0 && (
          <span style={{
            fontSize: 13, fontWeight: 600, marginLeft: 'auto',
            color: m.score_trend > 0 ? '#10b981' : '#ef4444',
          }}>
            {m.score_trend > 0 ? '\u2191' : '\u2193'} {Math.abs(m.score_trend)}
          </span>
        )}
      </div>
      <div style={{
        height: 4, background: '#f1f5f9', borderRadius: 2, marginBottom: 12, overflow: 'hidden',
      }}>
        <div style={{
          height: '100%', width: `${m.current_score}%`, background: mColor,
          borderRadius: 2, transition: 'width 0.5s ease',
        }} />
      </div>
      {m.key_issues.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap' as const, gap: 6 }}>
          {m.key_issues.map((issue, i) => (
            <span key={i} style={{
              background: '#fef3c7', color: '#92400e', padding: '3px 10px',
              borderRadius: 20, fontSize: 11, fontWeight: 500,
            }}>{issue}</span>
          ))}
        </div>
      )}
    </div>
  );
};

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getDashboard()
      .then((res) => setData(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8', fontSize: 15 }}>
      Загрузка...
    </div>
  );
  if (!data) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#ef4444', fontSize: 15 }}>
      Ошибка загрузки
    </div>
  );

  const scoreColor = getScoreColor(data.team_avg_score);

  const metrics = [
    {
      icon: '\u{1F3AF}',
      label: 'Средний балл',
      value: data.team_avg_score,
      valueColor: scoreColor,
      trend: data.team_score_trend,
      trendColor: data.team_score_trend >= 0 ? '#10b981' : '#ef4444',
    },
    {
      icon: '\u{1F4AC}',
      label: 'Диалогов сегодня',
      value: data.total_dialogues_today,
      valueColor: '#0f172a',
      trend: null,
      trendColor: '',
    },
    {
      icon: '\u{1F514}',
      label: 'Активные алерты',
      value: data.alerts.length,
      valueColor: data.alerts.length > 0 ? '#ef4444' : '#10b981',
      trend: null,
      trendColor: '',
    },
    {
      icon: '\u{1F4B0}',
      label: 'Упущенная выручка',
      value: data.missed_revenue ? `${(data.missed_revenue / 1000).toFixed(0)}K` : '0',
      valueColor: data.missed_revenue > 0 ? '#ef4444' : '#10b981',
      trend: null,
      trendColor: '',
    },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={styles.title}>Дашборд</h1>
        <p style={styles.subtitle}>Обзор эффективности команды</p>
      </div>

      {/* Metrics */}
      <div style={styles.metricsGrid}>
        {metrics.map((m, i) => (
          <div key={i} style={styles.metricCard}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={styles.metricLabel}>{m.label}</div>
              <span style={{ fontSize: 20 }}>{m.icon}</span>
            </div>
            <div style={{ ...styles.metricValue, color: m.valueColor }}>
              {m.value}
            </div>
            {m.trend !== null && (
              <div style={{ fontSize: 13, fontWeight: 600, color: m.trendColor, marginTop: 4 }}>
                {m.trend >= 0 ? '\u2191 +' : '\u2193 '}{m.trend} за неделю
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Score Chart */}
      <div style={styles.chartCard}>
        <div style={{ marginBottom: 20 }}>
          <h3 style={styles.cardTitle}>Динамика оценок</h3>
          <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Последние 30 дней</p>
        </div>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data.score_history}>
            <defs>
              <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6366f1" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
            <Tooltip
              contentStyle={{
                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 13,
              }}
            />
            <Area
              type="monotone" dataKey="score" stroke="#6366f1" strokeWidth={2.5}
              fill="url(#scoreGradient)" dot={false} activeDot={{ r: 5, fill: '#6366f1', strokeWidth: 2, stroke: '#fff' }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Team Section */}
      <div style={{ marginBottom: 28 }}>
        <h3 style={styles.sectionTitle}>Команда</h3>
        <div style={styles.managersGrid}>
          {data.managers.map((m) => (
            <ManagerCardItem
              key={m.id}
              m={m}
              onClick={() => navigate(`/manager/${m.id}`)}
            />
          ))}
        </div>
      </div>

      {/* Alerts */}
      {data.alerts.length > 0 && (
        <div>
          <h3 style={styles.sectionTitle}>Требует внимания</h3>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: 10 }}>
            {data.alerts.map((a) => (
              <div key={a.id} style={{
                background: '#fff',
                borderRadius: 12,
                padding: '16px 20px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 14,
              }}>
                <span style={{
                  fontSize: 20, lineHeight: 1, flexShrink: 0, marginTop: 2,
                }}>
                  {a.severity === 'critical' ? '\u{1F534}' : '\u{1F7E1}'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>{a.manager_name}</div>
                  <div style={{ fontSize: 13, color: '#475569', marginTop: 3, lineHeight: 1.5 }}>{a.message}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                    {new Date(a.created_at).toLocaleString('ru-RU')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '32px 36px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    color: '#0f172a',
    margin: 0,
    letterSpacing: '-0.3px',
  },
  subtitle: {
    fontSize: 15,
    color: '#94a3b8',
    margin: '4px 0 0 0',
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 16,
    marginBottom: 24,
  },
  metricCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  metricLabel: {
    fontSize: 13,
    color: '#94a3b8',
    fontWeight: 500,
    marginBottom: 10,
  },
  metricValue: {
    fontSize: 32,
    fontWeight: 700,
    color: '#0f172a',
    letterSpacing: '-0.5px',
  },
  chartCard: {
    background: '#fff',
    borderRadius: 12,
    padding: 24,
    border: '1px solid #e2e8f0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    marginBottom: 28,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#0f172a',
    margin: 0,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: '#0f172a',
    marginBottom: 16,
  },
  managersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
    gap: 16,
  },
};

export default Dashboard;
